import { useEffect, useMemo, useState } from 'react';
import { PATHS, momentRef, pathById } from '../../data';
import type { PathDef, PathFamily, ProtoEvent } from '../../data/types';
import { ruleOfTheOctave } from '../../data/rule';
import { keyName, parseKey } from '../../music/key';
import { formatRoman } from '../../music/roman';
import { FAMILY_LABELS } from '../copy';
import { usePlayer, usePrefs } from '../hooks';
import { VoiceFilter, protoLength, protoNotes, transposeKeyLabel } from '../model';
import { Glyph } from '../components/Glyph';
import { FlipIcon, PauseIcon, PlayIcon } from '../components/Icons';
import { Crumbs, SubTabs } from '../components/Shell';

/** Patterns can be browsed by name or by what they feel like. */
export function PatternsTabs({ current }: { current: 'paths' | 'feel' }) {
  return (
    <SubTabs
      current={current}
      items={[
        { id: 'paths', href: '#paths', label: 'All patterns' },
        { id: 'feel', href: '#feel', label: 'Browse by feeling' },
      ]}
    />
  );
}
import { instancesOf } from '../../data/corpus';
import { Excerpts } from '../components/Excerpts';
import type { PlayNote } from '../../audio/player';

const FAMILY_ORDER: PathFamily[] = ['cadence', 'prolongation', 'opening', 'answer', 'sequence', 'pedal', 'colour', 'grammar'];

function prototypeOf(p: PathDef): { proto: ProtoEvent[]; whatIf: ProtoEvent[] | null } {
  if (p.id === 'rule-of-the-octave') {
    const r = ruleOfTheOctave();
    return { proto: r.rule, whatIf: r.plain };
  }
  return { proto: p.prototype, whatIf: p.whatIf?.prototype.length ? p.whatIf.prototype : null };
}

export function PathsIndex() {
  const { prefs } = usePrefs();
  const named = prefs.depth >= 2;
  return (
    <div>
      <header className="page-head">
        <span className="label">Patterns</span>
        <h1>The patterns composers used</h1>
        <p>
          Composers took the same routes through harmony so often that the routes became a vocabulary, recognized by listeners without names. Open one to hear it on
          its own and in real music.
        </p>
        <p className="hint">Each card draws the pattern’s bass line; the dashed line is home.</p>
      </header>
      <PatternsTabs current="paths" />
      {FAMILY_ORDER.map((family) => {
        const items = PATHS.filter((p) => p.family === family);
        if (!items.length) return null;
        return (
          <section key={family} className="family">
            <span className="label">{FAMILY_LABELS[family]}</span>
            <div className="path-grid">
              {items.map((p) => {
                const { proto } = prototypeOf(p);
                return (
                  <a key={p.id} className="path-card" href={`#path.${p.id}`}>
                    <h3>{named ? p.name : p.feeling}</h3>
                    <span className="feel">{named ? p.feeling : ' '}</span>
                    <span className="glyph">
                      <Glyph events={proto} keyToken={p.key} width={180} height={56} title={`${p.name} bass line`} />
                    </span>
                  </a>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

export function PathDetail({ id }: { id: string }) {
  const path = pathById(id);
  if (!path) {
    return (
      <div className="page-head">
        <h1>No such pattern</h1>
        <p>
          <a href="#paths">See all paths</a>
        </p>
      </div>
    );
  }
  return <PathInner key={path.id} path={path} />;
}

function PathInner({ path }: { path: PathDef }) {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player, position, playing } = usePlayer();
  const { proto, whatIf } = useMemo(() => prototypeOf(path), [path]);
  const [useWhatIf, setUseWhatIf] = useState(false);
  const [voices, setVoices] = useState<VoiceFilter>('all');
  const [chorus, setChorus] = useState(false);
  const [flipped, setFlipped] = useState(depth >= 3);

  const events = useWhatIf && whatIf ? whatIf : proto;
  // Stable identity matters: a fresh [] on every render would rebuild the plan and restart the player every frame.
  const variants = useMemo(() => path.variants ?? [], [path.variants]);

  // Either one statement, or the chorus: the same path in several keys, one after another.
  const plan = useMemo(() => {
    const segments: Array<{ start: number; label: string; steps: number; semis: number }> = [{ start: 0, label: keyName(parseKey(path.key)), steps: 0, semis: 0 }];
    const len = protoLength(events);
    if (chorus) variants.forEach(([steps, semis, label], i) => segments.push({ start: (len + 1) * (i + 1), label, steps, semis }));
    const notes: PlayNote[] = segments.flatMap((s) => protoNotes(events, { steps: s.steps, semis: s.semis, offset: s.start, voices }));
    const total = segments[segments.length - 1].start + len;
    return { segments, notes, total, len };
  }, [events, chorus, variants, voices, path.key]);

  useEffect(() => {
    player.setNotes(plan.notes, plan.total);
  }, [player, plan]);
  useEffect(() => player.setTempo(prefs.tempo), [player, prefs.tempo]);

  const seg = [...plan.segments].reverse().find((s) => position >= s.start) ?? plan.segments[0];
  const local = position - seg.start;
  let acc = 0;
  let active = -1;
  if (playing || position > 0) {
    events.forEach((e, i) => {
      if (local >= acc && local < acc + e.dur) active = i;
      acc += e.dur;
    });
  }

  const heard = path.heardIn.map(momentRef).filter(Boolean) as Array<NonNullable<ReturnType<typeof momentRef>>>;

  return (
    <div>
      <Crumbs items={[{ href: '#paths', label: 'Patterns' }, { label: depth >= 2 ? path.name : path.feeling }]} />
      <div className="path-detail">
        <section className="panel">
          <span className="label">{FAMILY_LABELS[path.family]}</span>
          <h1 style={{ fontSize: 'clamp(1.9rem, 4vw, 2.6rem)', margin: '4px 0 2px', fontStyle: depth >= 2 ? 'normal' : 'italic' }}>{depth >= 2 ? path.name : path.feeling}</h1>
          {depth >= 2 && <p style={{ fontFamily: 'var(--display)', fontStyle: 'italic', fontSize: '1.2rem', color: 'var(--ink-2)', margin: 0 }}>{path.feeling}</p>}
          <div className="big-glyph">
            <Glyph events={events} keyToken={path.key} width={320} height={130} labels={depth >= 2} active={active} title={`${path.name}: bass line`} />
          </div>
          <p className="listen">{depth <= 1 && path.plain ? path.plain : path.listenFor}</p>
          <div className="actions" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <button className="play small" onClick={() => (playing ? player.pause() : void player.play(position >= plan.total - 0.01 ? 0 : undefined))} aria-label={playing ? 'Pause' : 'Play'}>
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            {variants.length > 0 && (
              <button className="btn" aria-pressed={chorus} onClick={() => { setChorus((c) => !c); player.seek(0); }} title="Hear the same pattern in several keys">
                Hear it in {variants.length + 1} keys
              </button>
            )}
            <div className="seg" role="group" aria-label="Voices">
              {(['all', 'outer', 'bass', 'melody'] as VoiceFilter[]).map((v) => (
                <button key={v} aria-pressed={voices === v} onClick={() => setVoices(v)}>
                  {v === 'all' ? 'All voices' : v === 'outer' ? 'Melody + bass' : v === 'bass' ? 'Bass only' : 'Melody only'}
                </button>
              ))}
            </div>
          </div>
          {chorus && (
            <p className="muted" style={{ marginTop: 10 }}>
              Now: <b style={{ color: 'var(--ink)' }}>{seg.label}</b>. Same pattern, different key: listen for what stays the same.
            </p>
          )}
          <div className="proto-steps" aria-label="Chords of the pattern; the one sounding is marked">
            {events.map((e, i) => (
              <span key={i} data-on={i === active}>
                {depth >= 3 ? formatRoman(e.rn) : depth >= 2 ? `${i + 1}` : '•'}
                {depth >= 3 && e.key ? <small className="muted"> ({e.key})</small> : null}
              </span>
            ))}
          </div>

          {path.whatIf && whatIf && (
            <section className="whatif">
              <h3>{path.whatIf.question}</h3>
              <p>{path.whatIf.effect}</p>
              <div className="ab" role="group" aria-label="Compare">
                <button aria-pressed={!useWhatIf} onClick={() => setUseWhatIf(false)}>
                  The path
                </button>
                <button aria-pressed={useWhatIf} onClick={() => setUseWhatIf(true)}>
                  What if: {path.whatIf.label.toLowerCase()}
                </button>
              </div>
            </section>
          )}
        </section>

        <section className="panel">
          <InstancesPanel path={path} />
          {heard.length > 0 && (
            <div className="chips" style={{ marginBottom: 14 }}>
              {heard.map(({ piece, moment }) => (
                <a key={moment.id} className="chip" href={`#${piece.id}.${moment.id}`}>
                  {piece.catalogue}, bars {moment.from}–{moment.to}: <i>{moment.feeling}</i>
                </a>
              ))}
            </div>
          )}
          <div className="panel-head" style={{ marginTop: 8 }}>
            <span className="label">What it is</span>
            {depth >= 1 && (
              <button className="btn quiet" onClick={() => setFlipped((f) => !f)} aria-expanded={flipped}>
                <FlipIcon /> {flipped ? 'Hide the theory' : 'Show the theory'}
              </button>
            )}
          </div>
          {flipped ? (
            <div>
              <p>{path.theory}</p>
              <dl className="formula">
                <dt>Bass</dt>
                <dd>{path.bassFormula}</dd>
                {path.melodyFormula && (
                  <>
                    <dt>Melody</dt>
                    <dd>{path.melodyFormula}</dd>
                  </>
                )}
                <dt>Harmony</dt>
                <dd>{path.harmonyFormula}</dd>
                <dt>Key here</dt>
                <dd>{keyName(parseKey(path.key))}{chorus ? `, then ${variants.map((v) => transposeKeyLabel(path.key, v[0], v[1])).join(', ')}` : ''}</dd>
              </dl>
              <p className="muted">{path.history}</p>
              {path.sources.length > 0 && (
                <ul className="sources">
                  {path.sources.map((s) => (
                    <li key={s.label}>{s.url ? <a href={s.url}>{s.label}</a> : s.label}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="muted">Listen a few times first. The name and the formula are one click away.</p>
          )}
        </section>
      </div>
    </div>
  );
}

/** Real occurrences of the path, found by rule across the atlas, playable one by one or as a chorus. */
function InstancesPanel({ path }: { path: PathDef }) {
  const all = useMemo(() => instancesOf(path.id).filter((i) => i.kind !== 'keyboard'), [path.id]);
  return (
    <>
      <div className="panel-head">
        <span className="label">Heard in real music{all.length ? ` · ${all.length}` : ''}</span>
      </div>
      {all.length ? (
        <Excerpts items={all} chorus={16} note="Found by rule in the Bach chorales and Mozart sonata movements. Each excerpt runs from just before the approach to the arrival; a chorus of 16 samples the whole list." />
      ) : (
        <p className="muted">No chorale or sonata movement in the atlas uses this pattern in a way the rules can recognize yet.</p>
      )}
    </>
  );
}
